// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Putting the virtual floor under the player (M5-33).
//
// **A platform recentre cannot do this, and that is the whole reason this file exists.** Meta's
// long-press re-centres the tracking origin and resets "forward"; with a floor-relative origin it
// changes nothing about height at all. The floor level of a `local-floor` space comes from the
// headset's room setup, and WebXR says a runtime that cannot find one "may do so by estimating a
// floor level, but may not present any UI" — so `y = 0` is only ever guaranteed to be *a* floor
// plane, never the player's.
//
// TWO FAULTS THAT LOOK THE SAME AND NEED OPPOSITE ANSWERS
// ------------------------------------------------------
// 1. **A stale floor.** The runtime goes on refining its estimate after the session starts and
//    does not reliably fire `reset` when it does, so a reference space captured at entry can be
//    out by half a metre. The answer is `adoptRuntimeFloor`: ask for the *current* floor again.
//    No plausibility check can catch this one — a stale floor reports a perfectly ordinary
//    standing height, it is simply the wrong ordinary standing height.
// 2. **A broken floor.** Space Setup has not localised, so the runtime invents a floor that is
//    wrong by metres and a standing player is reported with their eyes near the ground. The
//    answer is `alignToViewer`, which is the only lever that can move height at all:
//    `getOffsetReferenceSpace` moves the *world*.
//
// **Nothing here stores an offset.** An earlier attempt at this in a sister project measured the
// error once and baked it in as a constant; when the runtime later moved its own floor the
// constant was wrong in the other direction and put the player a metre *under* the room. The
// error is not stable — 3.07 m, 1.69 m and 1.74 m on three readings of one headset in one room —
// and the player's own height is. So `eyeHeightMemory.ts` remembers the person, and the
// correction is recomputed from a freshly re-read runtime space every single time. Applying a
// correction twice therefore lands in the same place as applying it once.
//
// A FOURTH FAULT, AND THE ONE THING THAT DOES NOT MOVE
// ----------------------------------------------------
// 4. **A floor that moves mid-session, silently, by a believable amount** (14 Sep 2026, the
//    23:11 VR session). The headset came off for twenty seconds and went back on; no `reset`
//    fired; a standing player who had read 1.68 m read 1.20 m from then on, and the cabinet's
//    playfield stood at their eyes. Believable, so answer 2 had nothing to say; no room scan in
//    VR, so `alignToFloorPlane` had nothing to measure. What *does* not move is the `local`
//    space: its origin is where the head was at the session's start, or at the last recentre —
//    which fires `reset`, and re-baselines this. So `holdAgainstLocal` reads the local origin's
//    height above the runtime's floor every frame, and when that number changes, the floor moved
//    and the player did not; the presented floor is put back where the session started. A player
//    sitting down changes nothing here — both spaces stay where they were.

import { observeEyeHeight } from './eyeHeightMemory'
import type { StorageLike } from '../storage/local'

/** The two calls on a reference space this needs, named locally so nothing else imports the DOM. */
export interface ReferenceSpacePort {
  getOffsetReferenceSpace?: (transform: unknown) => unknown
}

/** The renderer's side: where the current space is read and written. */
export interface FloorRendererPort {
  xr: {
    setReferenceSpace?: (space: unknown) => void
    getReferenceSpace?: () => unknown
  }
}

/** The session's side: where a fresh floor comes from. */
export interface FloorSessionPort {
  requestReferenceSpace?: (type: string) => Promise<unknown>
}

/** Builds the rigid transform a correction is applied through. Injected so this runs in Node. */
export type OffsetFactory = (y: number) => unknown

/** The frame's side: where one space is read against another. */
export interface FloorFramePort {
  getPose: (space: unknown, base: unknown) => { transform: { position: { y: number } } } | undefined | null
}

/** A local↔floor drift smaller than this is tracking noise, metres. */
export const HOLD_MIN = 0.12
/** Frames a drift must persist before the floor is held, so one bad frame moves nothing. */
export const HOLD_FRAMES = 24

/** What `holdAgainstLocal` did this frame, when it did anything. */
export interface FloorHold {
  /** `baseline`: the first reading; `held`: the floor moved and was put back. */
  readonly kind: 'baseline' | 'held'
  /** How far the runtime moved its floor *up* since the baseline, metres (negative: down). */
  readonly moved: number
  /** The local origin's height above the runtime's floor, as it now reads. */
  readonly localHeight: number
}

export interface FloorAlignmentOptions {
  readonly renderer: FloorRendererPort
  readonly storage?: StorageLike | undefined
  /**
   * How a `{ y }` translation becomes whatever `getOffsetReferenceSpace` wants.
   *
   * `XRRigidTransform` in a browser and a plain object in a test. A constructor rather than a
   * value because one is needed per correction and the DOM type is not constructible in Node.
   */
  readonly offset: OffsetFactory
}

/** What an alignment attempt did, so the caller can say something true about it. */
export interface FloorAlignmentResult {
  /** The correction applied, metres. Absent when the runtime's floor was believable. */
  readonly correction?: number
  /** True when the reported height was one a body could have, and was therefore remembered. */
  readonly believed: boolean
}

/**
 * The runtime's floor, and the corrections measured against it.
 *
 * Holds exactly one piece of state: the **unoffset** space last read from the runtime. Every
 * correction is computed against that, never against a space this class has already offset, which
 * is what makes the whole thing idempotent.
 */
export class FloorAlignment {
  private readonly renderer: FloorRendererPort
  private readonly storage: StorageLike | undefined
  private readonly offset: OffsetFactory
  /** The runtime's own floor as last re-read — never an offset space built from it. */
  private base: unknown
  /**
   * The two corrections measured against `base`, both absolute, so the presented space is always
   * `base` offset by their sum and neither compounds on the other. Cleared with `base`.
   */
  private eyeCorrection = 0
  private held = 0
  /** The eye-height step across a reset (`holdContinuity`), absolute against `base` like the other two. */
  private continuity = 0
  /** The local origin's height above `base` when it was first read (fault 4). */
  private localHeight: number | undefined
  private driftFrames = 0

  constructor(options: FloorAlignmentOptions) {
    this.renderer = options.renderer
    this.storage = options.storage
    this.offset = options.offset
  }

  /** Forget the runtime's floor. A new session re-reads it rather than trusting the old one. */
  reset(): void {
    this.base = undefined
    this.rebase()
  }

  /** A fresh base: every correction measured against the old one is void. */
  private rebase(): void {
    this.eyeCorrection = 0
    this.held = 0
    this.continuity = 0
    this.localHeight = undefined
    this.driftFrames = 0
  }

  /** Present `base` offset by both corrections. False on a runtime with no offset spaces. */
  private present(): boolean {
    const base = this.base as ReferenceSpacePort | undefined
    if (base?.getOffsetReferenceSpace === undefined) return false
    if (this.renderer.xr.setReferenceSpace === undefined) return false
    this.renderer.xr.setReferenceSpace(
      base.getOffsetReferenceSpace(this.offset(this.eyeCorrection + this.continuity + this.held)),
    )
    return true
  }

  /**
   * The player is the same height on both sides of a reset (15 Sep 2026) — the fifth answer.
   *
   * On a Quest a `reset` re-reads the floor, and the re-read can be a believable amount wrong:
   * the 23:47 pair of resets (the headset put back on) had a standing player who read 1.665 m
   * read 1.23 m, and the playfield stood at their eyes; the 23:11 session's silent move was the
   * same fault without the event. A body does not change height across an event that takes a
   * second, so the runtime's step in the reported eye height *is* the floor's error, and
   * `delta` (after − before, the `floorCorrection` sign) is applied against the freshly re-read
   * base. The plane rule reaches the same place in MR a moment later and, measuring the
   * presented space, then finds nothing to move.
   */
  holdContinuity(delta: number): boolean {
    if (!Number.isFinite(delta)) return false
    this.continuity = delta
    return this.present()
  }

  /**
   * Hold the floor where the session started, against a runtime that moves it silently (fault 4).
   *
   * Call every frame with the session's `local` space. The first reading is the baseline; from
   * then on a change in the local origin's height above `base` that lasts `HOLD_FRAMES` is the
   * runtime having moved its floor, and the presented floor is offset back by exactly that. Idempotent
   * the way the rest of this file is: the number is measured fresh against `base` every frame, so
   * a hold applied twice is the same hold. A `reset` re-reads `base` and starts again.
   */
  holdAgainstLocal(frame: FloorFramePort, local: unknown): FloorHold | undefined {
    if (this.base === undefined || local === undefined) return undefined
    const y = frame.getPose(local, this.base)?.transform.position.y
    if (y === undefined || !Number.isFinite(y)) return undefined
    if (this.localHeight === undefined) {
      this.localHeight = y
      return { kind: 'baseline', moved: 0, localHeight: y }
    }
    // The floor moving up by d reads the fixed local origin d lower: drift = −(floor moved up).
    const drift = y - this.localHeight
    if (Math.abs(drift - this.held) < HOLD_MIN) {
      this.driftFrames = 0
      return undefined
    }
    this.driftFrames += 1
    if (this.driftFrames < HOLD_FRAMES) return undefined
    this.driftFrames = 0
    this.held = drift
    if (!this.present()) return undefined
    return { kind: 'held', moved: -drift, localHeight: y }
  }

  /**
   * Put the presented floor on the room's floor plane (`floorPlane.ts`, 14 Sep 2026) — the third
   * fault, the one neither answer above reaches: a floor that is wrong by a *believable* amount.
   *
   * `planeY` is where the room scan's floor sits in the space being presented, so this is
   * measured in the presented space and applied to it — an increment, unlike `alignToViewer`'s
   * base-relative correction — and it is safe for the reason the header wants: the number is
   * read fresh every time, so the second application finds the plane at zero and is not made.
   * The corrected space becomes the base a later `alignToViewer` measures against, because it is
   * a fresh reading of where the floor is, which is all the base was ever meant to be.
   */
  alignToFloorPlane(planeY: number): boolean {
    const current = this.renderer.xr.getReferenceSpace?.() as ReferenceSpacePort | undefined
    if (current?.getOffsetReferenceSpace === undefined) return false
    if (this.renderer.xr.setReferenceSpace === undefined) return false
    const corrected = current.getOffsetReferenceSpace(this.offset(planeY))
    this.renderer.xr.setReferenceSpace(corrected)
    this.base = corrected
    this.rebase()
    return true
  }

  /**
   * Ask the runtime where the floor is *now* and present in that space.
   *
   * Answers the stale-floor fault. The space is kept unoffset, because `alignToViewer` measures
   * against it — offsetting it here would make the next correction compound on this one.
   *
   * Silent on a runtime that exposes neither call: a browser that cannot re-read its floor is a
   * browser whose floor was as good as it was going to get, and there is nothing to report.
   */
  async adoptRuntimeFloor(session: FloorSessionPort | undefined, type = 'local-floor'): Promise<boolean> {
    if (session?.requestReferenceSpace === undefined) return false
    if (this.renderer.xr.setReferenceSpace === undefined) return false
    let fresh: unknown
    try {
      fresh = await session.requestReferenceSpace(type)
    } catch {
      // The session can end while this is in flight. Keeping the space we had is correct.
      return false
    }
    this.base = fresh
    this.rebase()
    this.renderer.xr.setReferenceSpace(fresh)
    return true
  }

  /**
   * Correct a floor that cannot be true, and learn from one that can.
   *
   * **Call this a frame after `adoptRuntimeFloor`, not on the same one.** The viewer's pose is
   * reported in the space that was installed when the frame began, so reading a height on the
   * frame a new space is installed measures the player against the space just replaced — and
   * corrects for an error that is no longer there.
   *
   * `reportedEyeHeight` must come from the re-read space for the same reason.
   */
  alignToViewer(reportedEyeHeight: number): FloorAlignmentResult {
    const correction = observeEyeHeight(this.storage, reportedEyeHeight)
    if (correction === undefined) return { believed: true }
    // **Captured once, then never re-read from the renderer.** Falling back to whatever the
    // renderer is presenting *right now* is the compounding bug wearing a disguise: after one
    // correction the presented space is an offset one, and in a real browser that is still a
    // perfectly good `XRReferenceSpace` with its own `getOffsetReferenceSpace`. Offsetting it
    // again would double the correction and put the player under the room. So the first reading
    // is adopted as the base and every later correction is measured against that same space.
    if (this.base === undefined) this.base = this.renderer.xr.getReferenceSpace?.()
    this.eyeCorrection = correction
    if (!this.present()) return { believed: false }
    return { correction, believed: false }
  }
}
